
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type CsmMomentsBlurShaderUniforms = {
	uMomentsMap: WebGLUniformLocation,
	uDirection: WebGLUniformLocation,
	uTexelSize: WebGLUniformLocation
};

export type CsmMomentsBlurShaderAttributes = {
	aVertexPosition: number,
	vTexCoord: number
};

export type CsmMomentsBlurShaderSettings = string[];

export class CsmMomentsBlurShader extends ShaderBase<CsmMomentsBlurShaderUniforms, CsmMomentsBlurShaderAttributes> {

    private static fragmentShaderSource = `
in vec2 vTexCoord;
out vec4 fragColor;

uniform sampler2D uMomentsMap;
uniform vec2 uDirection;
uniform vec2 uTexelSize;

// 5-tap separable Gaussian on EVSM moments. Filtering moments preserves
// Chebyshev's bound (linearity of expectation), so a Gaussian on the moments
// texture is a valid soft-shadow filter. Run twice per cascade with
// uDirection = (1,0) then (0,1).
//
// Weights from a 1D Gaussian σ ≈ 1.0 truncated at ±2 texels and re-normalised:
//   center 0.38774, ±1 = 0.24477, ±2 = 0.06136 → sum 1.0.
const float W0 = 0.38774;
const float W1 = 0.24477;
const float W2 = 0.06136;

void main(void) {
	vec2 step = uDirection * uTexelSize;
	vec2 m = texture(uMomentsMap, vTexCoord).rg * W0;
	m += texture(uMomentsMap, vTexCoord + step).rg * W1;
	m += texture(uMomentsMap, vTexCoord - step).rg * W1;
	m += texture(uMomentsMap, vTexCoord + step * 2.0).rg * W2;
	m += texture(uMomentsMap, vTexCoord - step * 2.0).rg * W2;
	fragColor = vec4(m, 0.0, 0.0);
}
`;

    private static vertexShaderSource = `
// --- begin: fullscreen_quad.vert.glsl ---
layout(location = 0) in vec2 aVertexPosition;
out vec2 vTexCoord;

void main(void) {
	vTexCoord = aVertexPosition * 0.5 + 0.5;
	gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
// --- end: fullscreen_quad.vert.glsl ---
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: CsmMomentsBlurShaderUniformsContainer;
    public attributesContainer: CsmMomentsBlurShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: CsmMomentsBlurShaderSettings) {
        super(ctx, CsmMomentsBlurShader.fragmentShaderSource, CsmMomentsBlurShader.vertexShaderSource, settings);
        this.uniformsContainer = new CsmMomentsBlurShaderUniformsContainer(ctx, this);
        this.attributesContainer = new CsmMomentsBlurShaderShaderAttributesContainer(ctx, this);
    }
}

class CsmMomentsBlurShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: CsmMomentsBlurShader) {
        super(ctx);
    }

    public setMomentsMap(texture: Texture) : CsmMomentsBlurShaderUniformsContainer {
        this.setTextureValue('uMomentsMap', this.shader.uniforms.uMomentsMap, texture, 'sampler2D');
        return this;
    }

    public setDirection(value: vec2 | number[]) : CsmMomentsBlurShaderUniformsContainer {
        this.setValue('uDirection', this.shader.uniforms.uDirection, value, 'vec2');
        return this;
    }

    public setTexelSize(value: vec2 | number[]) : CsmMomentsBlurShaderUniformsContainer {
        this.setValue('uTexelSize', this.shader.uniforms.uTexelSize, value, 'vec2');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uMomentsMap', uniform: this.shader.uniforms.uMomentsMap, dataType: 'sampler2D' }
        ];
    }
}

class CsmMomentsBlurShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: CsmMomentsBlurShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : CsmMomentsBlurShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTexCoord(settings: { length: number, offset: number, itemSize: number }) : CsmMomentsBlurShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTexCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}